import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow,
  Chip, Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert, Snackbar,
  Tooltip, Switch, FormControlLabel,
} from '@mui/material';
import AddIcon    from '@mui/icons-material/Add';
import EditIcon   from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../utils/api';
import { useAuth } from '../utils/AuthContext';

const ROLES = ['readonly','operator','admin','super_admin'];
const ROLE_COLORS = { super_admin:'#7c3aed', admin:'#2f63ad', operator:'#16a34a', readonly:'#6b778c' };
const fmtDate = iso => iso ? new Date(iso).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}) : 'Never';

export default function Users() {
  const { user: me, hasRole } = useAuth();
  const [users, setUsers]   = useState([]);
  const [dialog, setDialog] = useState({ open:false, mode:'create', data:{ username:'', email:'', password:'', role:'operator' } });
  const [saving, setSaving] = useState(false);
  const [snack, setSnack]   = useState({ open:false, msg:'', sev:'success' });

  const load = async () => {
    const r = await api.get('/users');
    setUsers(r.data);
  };
  useEffect(() => { load(); }, []);

  const saveUser = async () => {
    setSaving(true);
    try {
      if (dialog.mode === 'create') await api.post('/users', dialog.data);
      else await api.put(`/users/${dialog.data.id}`, dialog.data);
      setSnack({ open:true, msg:'User saved', sev:'success' });
      setDialog(d=>({...d, open:false})); load();
    } catch(err) { setSnack({ open:true, msg:err.response?.data?.message||'Failed', sev:'error' }); }
    finally { setSaving(false); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await api.delete(`/users/${id}`); setSnack({ open:true, msg:'User deleted', sev:'info' }); load(); }
    catch(err) { setSnack({ open:true, msg:err.response?.data?.message||'Failed', sev:'error' }); }
  };

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Users & Access</Typography>
          <Typography variant="body2" color="text.secondary">Manage admin portal users</Typography>
        </Box>
        {hasRole('super_admin') && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setDialog({ open:true, mode:'create', data:{ username:'', email:'', password:'', role:'operator' } })}
            sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, boxShadow:'none' }}>
            Add User
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid', borderColor:'divider', overflow:'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor:'action.hover' }}>
              {['Username','Email','Role','Status','Created','Last Login','Actions'].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:'0.78rem', color:'text.secondary', py:1.5 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(u => (
              <TableRow key={u.id} hover sx={{ opacity: u.is_active ? 1 : 0.5 }}>
                <TableCell sx={{ py:1, fontWeight:600 }}>
                  {u.username} {u.id === me?.id && <Chip label="You" size="small" sx={{ height:16, fontSize:'0.65rem', ml:0.5 }} />}
                </TableCell>
                <TableCell sx={{ py:1, fontSize:'0.82rem' }}>{u.email}</TableCell>
                <TableCell sx={{ py:1 }}>
                  <Chip label={u.role.replace('_',' ')} size="small"
                    sx={{ height:22, fontSize:'0.7rem', fontWeight:700, backgroundColor:`${ROLE_COLORS[u.role]}18`, color:ROLE_COLORS[u.role], borderRadius:'6px' }} />
                </TableCell>
                <TableCell sx={{ py:1 }}>
                  <Chip label={u.is_active ? 'Active' : 'Inactive'} size="small"
                    sx={{ height:22, fontSize:'0.7rem', fontWeight:700,
                      backgroundColor: u.is_active ? 'rgba(22,163,74,0.1)' : 'rgba(220,38,38,0.1)',
                      color: u.is_active ? '#16a34a' : '#dc2626', borderRadius:'6px' }} />
                </TableCell>
                <TableCell sx={{ py:1, fontSize:'0.78rem', color:'text.secondary' }}>{fmtDate(u.created_at)}</TableCell>
                <TableCell sx={{ py:1, fontSize:'0.78rem', color:'text.secondary' }}>{fmtDate(u.last_login)}</TableCell>
                <TableCell sx={{ py:1 }}>
                  <Box sx={{ display:'flex', gap:0.5 }}>
                    {hasRole('admin') && (
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => setDialog({ open:true, mode:'edit', data:{...u, password:''} })}>
                          <EditIcon sx={{ fontSize:18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {hasRole('super_admin') && u.id !== me?.id && (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteUser(u.id)}>
                          <DeleteIcon sx={{ fontSize:18, color:'error.main' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* User dialog */}
      <Dialog open={dialog.open} onClose={() => setDialog(d=>({...d,open:false}))} maxWidth="xs" fullWidth PaperProps={{ sx:{borderRadius:'16px'} }}>
        <DialogTitle sx={{ fontWeight:700 }}>{dialog.mode==='create' ? 'Add User' : 'Edit User'}</DialogTitle>
        <DialogContent sx={{ pt:1 }}>
          {['username','email'].map(f => (
            <TextField key={f} fullWidth size="small" label={f.charAt(0).toUpperCase()+f.slice(1)}
              value={dialog.data[f]||''} onChange={e=>setDialog(d=>({...d,data:{...d.data,[f]:e.target.value}}))}
              sx={{ mb:2 }} disabled={dialog.mode==='edit' && f==='username'} />
          ))}
          <TextField fullWidth size="small" type="password"
            label={dialog.mode==='create' ? 'Password *' : 'New Password (leave blank to keep)'}
            value={dialog.data.password||''} onChange={e=>setDialog(d=>({...d,data:{...d.data,password:e.target.value}}))}
            sx={{ mb:2 }} />
          <FormControl fullWidth size="small" sx={{ mb:2 }}>
            <InputLabel>Role</InputLabel>
            <Select value={dialog.data.role||'operator'} label="Role"
              onChange={e=>setDialog(d=>({...d,data:{...d.data,role:e.target.value}}))}>
              {ROLES.map(r => <MenuItem key={r} value={r}>{r.replace('_',' ')}</MenuItem>)}
            </Select>
          </FormControl>
          {dialog.mode==='edit' && (
            <FormControlLabel control={<Switch checked={!!dialog.data.is_active}
              onChange={e=>setDialog(d=>({...d,data:{...d.data,is_active:e.target.checked}}))} />} label="Active" />
          )}
        </DialogContent>
        <DialogActions sx={{ px:3, pb:2.5 }}>
          <Button onClick={() => setDialog(d=>({...d,open:false}))} sx={{ textTransform:'none', color:'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={saveUser} disabled={saving} sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, boxShadow:'none' }}>
            {saving ? 'Saving...' : dialog.mode==='create' ? 'Create User' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s=>({...s,open:false}))} anchorOrigin={{ vertical:'top', horizontal:'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s=>({...s,open:false}))} sx={{ borderRadius:'12px' }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
